import { NavContext } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAuth } from '@/contexts/AuthenticationContext';
import { useDentist } from '@/contexts/DentistContext';
import { useTracker } from '@/contexts/TrackerContext';
import { Clinic, createClinic, getClinic } from '@/data/Clinics';
import {
  getDentistsByMobileNumber,
  getDentistsByUserId,
  updateDentist
} from '@/data/Dentists';
import { useToast } from '@/hooks';
import { GET_STARTED_STEPS, OTP_LOADING_DELAY_IN_MS } from '@/utils/constants';
import { OTP_ERRORS } from '@/utils/constants';
import { ROUTES } from '@/utils/routes';
import { supabase } from '@/utils/supabase';
import { EVENTS } from '@/utils/tracker';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface GetStartedContextProps {
  loading: boolean;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  goBack: () => void;
  clinicName: string | null;
  setClinicName: React.Dispatch<React.SetStateAction<string | null>>;
  mobileNumber: string | null;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  isResendDisabled: boolean;
  setIsResendDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const GetStartedContext = createContext<GetStartedContextProps | undefined>(
  undefined
);

interface GetStartedProviderProps {
  children: React.ReactNode;
}

export const GetStartedProvider: React.FC<GetStartedProviderProps> = ({
  children
}) => {
  const { navigate, goBack } = useContext(NavContext);
  const { authenticated, user, signIn } = useAuth();
  const { selectDentist } = useDentist();
  const { captureEvent } = useTracker();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [loadingVerification, setLoadingVerification] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<string>(
    GET_STARTED_STEPS.CLINIC_NAME
  );
  const [clinicName, setClinicName] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);

  const onDone = useCallback(async () => {
    selectDentist(null);
    navigate(ROUTES.SELECT_DENTIST);
  }, [navigate, selectDentist]);

  const sendOtpMutation = useMutation({
    mutationFn: async () => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      if (authenticated && `63${mobileNumber}` === user?.phone) {
        await signIn(false);
        await onDone();
        return { skipped: true };
      }

      setCurrentStep(GET_STARTED_STEPS.VERIFICATION);
      setLoadingVerification(true);
      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'send-with-owners',
          payload: {
            phone_number: `63${mobileNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error sending OTP: ${error.message}`);
      }

      return { skipped: false };
    },
    onSuccess: (data) => {
      setIsResendDisabled(true);
      if (data.skipped) {
        displayToast({
          message: 'Skipped verification.',
          duration: 2400,
          position: 'bottom',
          positionAnchor: 'next-button'
        });
      }
    },
    onError: (error: Error) => {
      if (error.message === OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED) {
        setIsResendDisabled(true);
        displayToast({
          message: error.message,
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'next-button'
        });
        return;
      }

      setIsResendDisabled(false);
      displayToast({
        message: OTP_ERRORS.FAILED_TO_SEND_OTP,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const resendOtpMutation = useMutation({
    mutationFn: async () => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'resend-with-owners',
          payload: {
            phone_number: `63${mobileNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error sending OTP: ${error.message}`);
      }
    },
    onSuccess: () => {
      setIsResendDisabled(true);
    },
    onError: (error: Error) => {
      if (error.message === OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED) {
        setIsResendDisabled(true);
        displayToast({
          message: error.message,
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'next-button'
        });
        return;
      }

      setIsResendDisabled(false);
      displayToast({
        message: OTP_ERRORS.FAILED_TO_RESEND_OTP,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (otp: string) => {
      if (!mobileNumber) throw new Error(OTP_ERRORS.MOBILE_NUMBER_IS_REQUIRED);

      const {
        data: { user },
        error: verifyError
      } = await supabase.auth.verifyOtp({
        phone: `63${mobileNumber}`,
        token: otp,
        type: 'sms'
      });

      if (verifyError) {
        throw new Error(`Error verifying OTP: ${verifyError.message}`);
      }

      if (!user) {
        throw new Error(OTP_ERRORS.NO_CLINIC_OR_DENTIST_FOUND);
      }

      const existingClinic = await queryClient.fetchQuery({
        queryKey: ['clinic', user.id],
        queryFn: () => getClinic(user.id)
      });
      if (!existingClinic) {
        const existingDentistsByUserId = await queryClient.fetchQuery({
          queryKey: ['dentistsByUserId', user.id],
          queryFn: () => getDentistsByUserId(user.id)
        });
        const existingDentistsByMobileNumber = await queryClient.fetchQuery({
          queryKey: ['dentistsByMobileNumber', mobileNumber],
          queryFn: () => getDentistsByMobileNumber(mobileNumber)
        });
        if (
          existingDentistsByUserId.length === 0 &&
          existingDentistsByMobileNumber.length === 0 &&
          clinicName &&
          mobileNumber
        ) {
          const newClinic = await queryClient.fetchQuery({
            queryKey: ['createClinic', user.id],
            queryFn: () =>
              createClinic({
                id: user.id,
                name: clinicName,
                mobile_number: mobileNumber
              } satisfies Clinic)
          });

          if (!newClinic) {
            throw new Error(OTP_ERRORS.FAILED_TO_CREATE_CLINIC);
          }

          captureEvent({
            event: EVENTS.USER_SIGNED_UP,
            properties: {
              user_id: user.id,
              clinic_name: clinicName,
              mobile_number: mobileNumber
            }
          });
        }
      }

      const existingDentists = await queryClient.fetchQuery({
        queryKey: ['dentistsByMobileNumber', mobileNumber],
        queryFn: () => getDentistsByMobileNumber(mobileNumber)
      });
      if (existingDentists.length > 0) {
        const userRecord = await signIn(false);

        if (userRecord) {
          const updatePromises = existingDentists.map((dentist) =>
            dentist.id
              ? updateDentist({
                  dentistId: dentist.id,
                  dentist: { ...dentist, user_id: userRecord.id }
                })
              : null
          );

          await Promise.all(updatePromises);
        }

        await onDone();
        return user;
      }

      await signIn(false);
      await onDone();
      return user;
    },
    onError: (error: Error) => {
      displayToast({
        message: error.message,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  });

  const sendOtp = useCallback(async () => {
    await sendOtpMutation.mutateAsync();
  }, [sendOtpMutation]);

  const resendOtp = useCallback(async () => {
    await resendOtpMutation.mutateAsync();
  }, [resendOtpMutation]);

  const verifyOtp = useCallback(
    async (otp: string) => {
      await verifyOtpMutation.mutateAsync(otp).catch(() => {});
    },
    [verifyOtpMutation]
  );

  // Show a brief loading animation for verification page
  useEffect(() => {
    if (loadingVerification) {
      setTimeout(() => {
        setLoadingVerification(false);
      }, OTP_LOADING_DELAY_IN_MS);
    }
  }, [loadingVerification]);

  useEffect(() => {
    return () => {
      setClinicName(null);
      setCurrentStep(GET_STARTED_STEPS.CLINIC_NAME);
      setLoadingVerification(false);
      setMobileNumber(null);
    };
  }, []);

  const loading =
    loadingVerification ||
    resendOtpMutation.isPending ||
    verifyOtpMutation.isPending;

  return (
    <GetStartedContext.Provider
      value={{
        loading,
        currentStep,
        setCurrentStep,
        goBack,
        clinicName,
        setClinicName,
        mobileNumber,
        setMobileNumber,
        sendOtp,
        resendOtp,
        verifyOtp,
        isResendDisabled,
        setIsResendDisabled
      }}
    >
      {children}
    </GetStartedContext.Provider>
  );
};

export const useGetStarted = () => {
  const context = useContext(GetStartedContext);
  if (context === undefined) {
    throw new Error('useGetStarted must be used within a GetStartedProvider');
  }
  return context;
};
